import { useQuery } from '@tanstack/react-query'
import { ServerIcon, SignalIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import api from '../services/api'

interface MonitoringData {
  online_devices: number
  offline_devices: number
  total_devices: number
  recent_alerts: Array<{
    id: string
    device_name: string
    message: string
    timestamp: string
    severity: string
  }>
}

export default function DeviceMonitoring() {
  const { data: monitoring, isLoading } = useQuery<MonitoringData>({
    queryKey: ['device-monitoring'],
    queryFn: async () => {
      const response = await api.get('/monitoring/devices')
      return response.data
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  })

  // Mock data for the chart
  const chartData = [
    { time: '00:00', online: 45, offline: 5 },
    { time: '04:00', online: 42, offline: 8 },
    { time: '08:00', online: 48, offline: 2 },
    { time: '12:00', online: 47, offline: 3 },
    { time: '16:00', online: 46, offline: 4 },
    { time: '20:00', online: 45, offline: 5 },
  ]

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Device Monitoring</h1>
        <p className="mt-1 text-sm text-gray-500">
          Real-time monitoring of your device infrastructure
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 rounded-md bg-green-500 p-3">
              <ServerIcon className="h-6 w-6 text-white" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Online Devices</dt>
                <dd className="text-2xl font-semibold text-gray-900">{monitoring?.online_devices || 0}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 rounded-md bg-red-500 p-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-white" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Offline Devices</dt>
                <dd className="text-2xl font-semibold text-gray-900">{monitoring?.offline_devices || 0}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 rounded-md bg-blue-500 p-3">
              <SignalIcon className="h-6 w-6 text-white" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Total Devices</dt>
                <dd className="text-2xl font-semibold text-gray-900">{monitoring?.total_devices || 0}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Device Status Chart */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Device Status Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="online" stroke="#10b981" strokeWidth={2} />
              <Line type="monotone" dataKey="offline" stroke="#ef4444" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Recent Alerts */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Alerts</h3>
          <div className="space-y-3">
            {monitoring?.recent_alerts?.length ? (
              monitoring.recent_alerts.map((alert) => (
                <div key={alert.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{alert.device_name}</p>
                    <p className="text-sm text-gray-500">{alert.message}</p>
                    <p className="text-xs text-gray-400">{new Date(alert.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No recent alerts</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}